import type { BillingInterval, BillingPlan, PlanChoice } from 'definitions/billing-plans';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  BILLING_PLAN_CATALOGUE,
  BILLING_TRIAL_DAYS,
  planChangeDirection,
  planPeriodPrice,
} from 'definitions/billing-plans';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { formatShortDate } from '@/state/onboarding/plan-model';
import { describeChoice, type PlanSummaryModel } from './plan-summary';
import { useBillingState } from './useBillingState';
import { useCheckout } from './useCheckout';
import { readApiError } from './api-error';
import { formatBillingDate, formatEuro } from './billing-format';

type PlanChangeDirection = 'upgrade' | 'downgrade';

type UsePlanPickerParams = {
  /** An agency's client whose plan this is; the caller's own account when omitted. */
  accountId?: number | null;
};

/**
 * The plan picker on Settings > Billing: the five plans at the chosen interval, and, once a
 * different one is picked, a summary of exactly what accepting it does. Nothing is charged or
 * changed by picking a card; only the button on the summary does it.
 *
 * Without a plan, accepting goes to Polar's checkout, with the trial if it is still unused.
 * With one, it changes the plan through the server, whose rules (prorationFor) decide what is
 * charged and when: during the trial any change is immediate and free, an upgrade charges the
 * difference now, and a downgrade waits for the period end. The summary says the same.
 *
 * Buying and changing are for Owners and Admins of whoever pays (an agency, for its clients'
 * plans); the server refuses anyone else (403) whatever this shows. With `accountId` the picker
 * is for one of an agency's clients: every call carries it, and the server checks it against
 * the agency's own clients.
 */
export const usePlanPicker = ({ accountId = null }: UsePlanPickerParams = {}) => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { billingState } = useBillingState({ accountId });
  const checkout = useCheckout();

  const [chosenInterval, setChosenInterval] = useState<BillingInterval | null>(null);
  const [picked, setPicked] = useState<BillingPlan | null>(null);

  const canManage = !!user && user.role !== 'Member' && (billingState?.isPayer ?? true);
  const current: PlanChoice | null =
    billingState?.hasLiveSubscription && billingState.plan && billingState.interval
      ? { plan: billingState.plan, interval: billingState.interval }
      : null;
  const hasPlan = !!current;
  const pendingChange = billingState?.pendingChange ?? null;
  const interval = chosenInterval ?? current?.interval ?? 'year';
  const isTrialing = billingState?.subscriptionStatus === 'trialing';
  const trialAvailable = !hasPlan && !!billingState?.trialAvailable;
  const checkoutAvailable = billingState?.checkoutAvailable ?? false;

  const isOnPlan = (plan: BillingPlan) => !!current && current.plan === plan && current.interval === interval;
  const isScheduled = (plan: BillingPlan) =>
    !!pendingChange && pendingChange.plan === plan && pendingChange.interval === interval;

  // A card that is already the plan, or already scheduled, has nothing to accept.
  const choice: PlanChoice | null =
    picked && !isOnPlan(picked) && !isScheduled(picked) ? { plan: picked, interval } : null;
  const direction: PlanChangeDirection | null =
    choice && current ? (planChangeDirection(current, choice) === 'downgrade' ? 'downgrade' : 'upgrade') : null;

  // Everything on the billing page reads the plan: the allowance, the invoices and the usage as well as the state.
  const afterChange = () =>
    queryClient.invalidateQueries({
      predicate: (query) => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('BILLING_'),
    });

  const toastFailure = (title: string) => (error: unknown) => {
    const { status, message } = readApiError(error);
    toast.error(title, {
      description: status && status >= 400 && status < 500 && message ? message : 'Please try again in a moment.',
    });
  };

  const accountParam = accountId ? `?accountCompanyId=${accountId}` : '';

  const changeMutation = useMutation({
    mutationFn: (next: PlanChoice) =>
      apiClient.post<{ direction: PlanChangeDirection; appliesNow: boolean }>(
        `/billing/change-plan${accountParam}`,
        next,
      ),
    onSuccess: (result, next) => {
      const name = BILLING_PLAN_CATALOGUE[next.plan].name;
      toast.success(
        result.appliesNow
          ? `You are now on ${name}.`
          : `${name} starts on ${formatBillingDate(billingState?.currentPeriodEnd)}. Until then, nothing changes.`,
      );
      setPicked(null);
    },
    onError: toastFailure('Could not change the plan'),
    onSettled: afterChange,
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiClient.post(`/billing/change-plan/cancel${accountParam}`),
    onSuccess: () => toast.success('The scheduled change is cancelled. The plan stays as it is.'),
    onError: toastFailure('Could not cancel the scheduled change'),
    onSettled: afterChange,
  });

  const summary: PlanSummaryModel | null = choice
    ? describeChoice({
        choice,
        direction,
        hasPlan,
        isTrialing,
        trialAvailable,
        trialEnd: billingState?.trialEnd ?? null,
        currentPeriodEnd: billingState?.currentPeriodEnd ?? null,
        currentName: current ? BILLING_PLAN_CATALOGUE[current.plan].name : null,
        checkoutAvailable,
      })
    : null;

  const accept = () => {
    if (!choice || !canManage) return;
    if (!hasPlan) checkout.startCheckout({ plan: choice.plan, interval: choice.interval, accountId });
    else changeMutation.mutate(choice);
  };

  return {
    canManage,
    hasPlan,
    interval,
    setInterval: setChosenInterval,
    selected: picked ?? null,
    select: setPicked,
    currentOnShow: current && current.interval === interval ? current.plan : null,
    scheduledOnShow: pendingChange && pendingChange.interval === interval ? pendingChange.plan : null,
    pendingChange,
    cancelPendingChange: () => cancelMutation.mutate(),
    isCancelling: cancelMutation.isPending,
    summary,
    accept,
    clearChoice: () => setPicked(null),
    isAccepting: changeMutation.isPending || checkout.isStarting,
    canAccept: canManage && (hasPlan || checkoutAvailable),
    trialAvailable,
  };
};

export type PlanPickerManager = ReturnType<typeof usePlanPicker>;
